import React from "react";
import { TerminalQuestion } from "./Terminal";
import HotspotYesNo from "./HotspotYesNo";
import DragDrop from "./DragDrop";
import DragDropDivergent from "./DragDropDivergent";

/** Correct answers map may use string or number keys from the API. */
export function lookupRespuestaCorrecta(map, preguntaId) {
  if (preguntaId == null || preguntaId === undefined) return undefined;
  return map[preguntaId] ?? map[String(preguntaId)] ?? map[Number(preguntaId)];
}

/** Build a Set of correct option ids for multiple_choice. */
export function buildCorrectMcIdsSet(preg, apiVal) {
  const ids = new Set();
  const add = (v) => {
    if (v == null || v === "") return;
    const n = Number(v);
    if (!Number.isNaN(n)) ids.add(n);
  };
  if (apiVal != null) {
    if (Array.isArray(apiVal)) {
      apiVal.forEach(add);
    } else if (typeof apiVal !== "object") {
      add(apiVal);
    }
  }
  const opciones = preg?.opciones || [];
  if (ids.size === 0) {
    opciones.filter((o) => o.correcta).forEach((o) => add(o.id));
  }
  return ids;
}

export function optionIdInCorrectMcSet(opcion, idSet) {
  const n = Number(opcion.id);
  return (!Number.isNaN(n) && idSet.has(n)) || idSet.has(opcion.id);
}

/**
 * Renderiza el área de respuesta interactiva para cualquier tipo de pregunta.
 */
export function PreguntaInteractiva({
  pregunta,
  respuestas,
  respuestasCorrectas = {},
  onSelectOpcion,
  onSelectHotspotOpcion,
  onSelectDragDropZone,
  disabled = false,
  showCorrectAnswer = false,
}) {
  if (!pregunta) return null;

  let tipoPregunta = pregunta.tipo_pregunta;
  if (pregunta.tipo_pregunta === "case_study") {
    tipoPregunta = pregunta.case_study_data?.pregunta_principal_tipo || "multiple_choice";
  }

  const preguntaParaRenderizar = pregunta;
  const locked = disabled || showCorrectAnswer;

  if (tipoPregunta === "drag_drop") {
    return (
      <DragDrop
        pregunta={preguntaParaRenderizar}
        respuestas={respuestas}
        onSelectDropZone={onSelectDragDropZone}
        disabled={locked}
        showCorrectAnswer={showCorrectAnswer}
        respuestaCorrecta={lookupRespuestaCorrecta(respuestasCorrectas, pregunta.id)}
      />
    );
  }

  if (tipoPregunta === "dragdrop_divergent") {
    return (
      <DragDropDivergent
        pregunta={preguntaParaRenderizar}
        respuestas={respuestas}
        onSelectDropZone={onSelectDragDropZone}
        disabled={locked}
        showCorrectAnswer={showCorrectAnswer}
        respuestaCorrecta={lookupRespuestaCorrecta(respuestasCorrectas, pregunta.id)}
      />
    );
  }

  if (tipoPregunta === "hotspot_yesno") {
    const opcionesPorDropdown = {};
    (preguntaParaRenderizar.opciones || []).forEach((opcion) => {
      if (opcion.dropdown_id) {
        if (!opcionesPorDropdown[opcion.dropdown_id]) {
          opcionesPorDropdown[opcion.dropdown_id] = [];
        }
        opcionesPorDropdown[opcion.dropdown_id].push(opcion);
      }
    });
    return (
      <HotspotYesNo
        pregunta={preguntaParaRenderizar}
        opcionesPorDropdown={opcionesPorDropdown}
        respuestas={respuestas}
        onSelectDropdown={onSelectHotspotOpcion}
        disabled={locked}
        showCorrectAnswer={showCorrectAnswer}
        respuestaCorrecta={lookupRespuestaCorrecta(respuestasCorrectas, pregunta.id)}
      />
    );
  }

  if (tipoPregunta === "hotspot_terminal") {
    const opcionesPorDropdown = {};
    (preguntaParaRenderizar.opciones || []).forEach((opcion) => {
      if (opcion.dropdown_id) {
        if (!opcionesPorDropdown[opcion.dropdown_id]) {
          opcionesPorDropdown[opcion.dropdown_id] = [];
        }
        opcionesPorDropdown[opcion.dropdown_id].push(opcion);
      }
    });
    return (
      <TerminalQuestion
        pregunta={preguntaParaRenderizar}
        opcionesPorDropdown={opcionesPorDropdown}
        respuestas={respuestas}
        onSelectDropdown={onSelectHotspotOpcion}
        disabled={locked}
        showCorrectAnswer={showCorrectAnswer}
        respuestaCorrecta={lookupRespuestaCorrecta(respuestasCorrectas, pregunta.id)}
      />
    );
  }

  if (tipoPregunta === "hotspot") {
    const opcionesPorDropdown = {};
    (preguntaParaRenderizar.opciones || []).forEach((opcion) => {
      if (opcion.dropdown_id) {
        if (!opcionesPorDropdown[opcion.dropdown_id]) {
          opcionesPorDropdown[opcion.dropdown_id] = [];
        }
        opcionesPorDropdown[opcion.dropdown_id].push(opcion);
      }
    });
    const dropdownIds = Object.keys(opcionesPorDropdown)
      .map((id) => parseInt(id))
      .sort((a, b) => a - b);
    const respuestaHotspot = respuestas[pregunta.id];
    const respuestaCorrectaHotspot = lookupRespuestaCorrecta(respuestasCorrectas, pregunta.id);
    const mostrarFeedbackHotspot =
      showCorrectAnswer &&
      typeof respuestaCorrectaHotspot === "object" &&
      respuestaCorrectaHotspot !== null;

    return (
      <div className="hotspot-answer-area-container">
        <div className="hotspot-answer-area-header">
          <h3 className="answer-area-title">Answer Area</h3>
        </div>
        <div className="hotspot-container">
          {dropdownIds.map((dropdownId) => {
            const opciones = opcionesPorDropdown[dropdownId].sort((a, b) => a.orden - b.orden);
            let opcionSeleccionada = null;
            if (
              typeof respuestaHotspot === "object" &&
              respuestaHotspot !== null &&
              !Array.isArray(respuestaHotspot) &&
              typeof respuestaHotspot !== "number"
            ) {
              opcionSeleccionada = respuestaHotspot[`dropdown_${dropdownId}`] || null;
            }
            const correctaId = mostrarFeedbackHotspot
              ? respuestaCorrectaHotspot[`dropdown_${dropdownId}`]
              : null;
            const opcionCorrecta =
              correctaId != null ? opciones.find((o) => o.id === correctaId) : null;
            const esCorrectoDropdown = correctaId != null && opcionSeleccionada === correctaId;
            const claseFeedbackDropdown = mostrarFeedbackHotspot
              ? esCorrectoDropdown
                ? " hotspot-dropdown-correcto"
                : opcionSeleccionada
                ? " hotspot-dropdown-incorrecto"
                : ""
              : "";
            const enunciado =
              preguntaParaRenderizar.dropdowns_enunciados &&
              preguntaParaRenderizar.dropdowns_enunciados[dropdownId]
                ? preguntaParaRenderizar.dropdowns_enunciados[dropdownId]
                : `Dropdown ${dropdownId}:`;

            return (
              <div key={dropdownId} className={`hotspot-dropdown-group${claseFeedbackDropdown}`}>
                <label className="hotspot-dropdown-label">{enunciado}</label>
                <select
                  className={`hotspot-dropdown${claseFeedbackDropdown}`}
                  value={opcionSeleccionada || ""}
                  onChange={(e) => {
                    if (showCorrectAnswer) return;
                    const selectedValue = e.target.value;
                    if (!disabled && selectedValue) {
                      const selectedOpcionId = parseInt(selectedValue);
                      if (!isNaN(selectedOpcionId)) {
                        onSelectHotspotOpcion(pregunta.id, dropdownId, selectedOpcionId);
                      }
                    }
                  }}
                  disabled={locked}
                >
                  <option value="">-- Select an option --</option>
                  {opciones.map((opcion) => (
                    <option key={opcion.id} value={opcion.id}>
                      {opcion.texto}
                    </option>
                  ))}
                </select>
                {mostrarFeedbackHotspot && opcionCorrecta && (
                  <div className="hotspot-respuesta-correcta-inline">
                    Correct answer: <strong>{opcionCorrecta.texto}</strong>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // multiple_choice (default)
  const resp =
    respuestas[pregunta.id] ??
    respuestas[String(pregunta.id)] ??
    respuestas[Number(pregunta.id)];
  const isMultiSelect =
    (preguntaParaRenderizar.opciones?.filter((o) => o.correcta).length ?? 0) > 1;
  const rcMc = lookupRespuestaCorrecta(respuestasCorrectas, pregunta.id);
  const correctIdSet = buildCorrectMcIdsSet(preguntaParaRenderizar, rcMc);

  return (
    <div className="multiple-choice-answer-area-container">
      <div className="multiple-choice-answer-area-header">
        <h3 className="answer-area-title">Answer Area</h3>
      </div>
      <div className="opciones-list">
        {(preguntaParaRenderizar.opciones || []).map((opcion) => {
          const isSelected = isMultiSelect
            ? Array.isArray(resp)
              ? resp.includes(opcion.id) || resp.includes(Number(opcion.id))
              : resp === opcion.id || Number(resp) === Number(opcion.id)
            : resp === opcion.id || Number(resp) === Number(opcion.id);
          const revealedCorrect = showCorrectAnswer && optionIdInCorrectMcSet(opcion, correctIdSet);
          const revealedWrong =
            showCorrectAnswer && isSelected && !optionIdInCorrectMcSet(opcion, correctIdSet);
          const itemClass = [
            "opcion-item",
            isSelected ? "opcion-seleccionada" : "",
            revealedCorrect ? "opcion-correcta" : "",
            revealedWrong ? "opcion-incorrecta" : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <div
              key={opcion.id}
              className={itemClass}
              onClick={() => !locked && onSelectOpcion(pregunta.id, opcion.id)}
            >
              <div className={`opcion-circulo ${isSelected ? "circulo-seleccionado" : ""}`}>
                {isSelected && <div className="circulo-interno"></div>}
              </div>
              <span className="opcion-texto">{opcion.texto}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PreguntaInteractiva;
